import type { TeamWithAssociations } from '../../shared/types/routes/teams';
import type { Team } from '../types';

/**
 * Returns the total number of projects and release groups in the team passed in.
 * We refer to this as the number of "projects" in a few places in the app even though this includes release groups.
 *
 * @param {Team} [team] - The team to get the total project count for.
 * @returns {number} The total number of projects and release groups in the team.
 */
export const totalProjectCount = (team?: Team | TeamWithAssociations): number => {
  if (!team) {
    return 0;
  }

  // For Team type, use teamProjects.length if available, otherwise fall back to count
  // For TeamWithAssociations, use teamProjectsCount which is the only available property
  const projectCount = 'teamProjects' in team && team.teamProjects
    ? team.teamProjects.length
    : team.teamProjectsCount ?? 0;

  return projectCount;
};

/**
 * Returns the total number of release groups in the team passed in.
 *
 * @param {Team} [team] - The team to get the total release group count for.
 * @returns {number} The total number of release groups in the team.
 */
export const totalReleaseGroupCount = (team?: Team | TeamWithAssociations): number => {
  if (!team) {
    return 0;
  }
  // For Team type, use teamReleaseGroups.length if available, otherwise fall back to count
  // For TeamWithAssociations, use teamReleaseGroupsCount which is the only available property
  const releaseGroupCount = 'teamReleaseGroups' in team && team.teamReleaseGroups
    ? team.teamReleaseGroups.length
    : team.teamReleaseGroupsCount ?? 0;

  return releaseGroupCount;
};
